import { useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { Invite } from '@matech/thebigpos-sdk'
import queryKeys from '@/services/queryKeys'
import { useAlert } from '.'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { getErrorMessage } from '../services/helper'

const useLoanInvites = (loanId?: string) => {
	const { alert } = useAlert()
	const { formatMessage } = useIntl()

	const loanInvitesQuery = useQuery({
		queryKey: [queryKeys.loanInvites, loanId],
		queryFn: () => TheBigPOSApi.getLoanInvites(loanId!),
		enabled: !!loanId,
	})

	const resendInviteMutation = useMutation({
		mutationFn: (data: string) =>
			TheBigPOSApi.resendInviteNotification(data),
	})

	const inviteContactsMutation = useMutation({
		mutationFn: (data: { loanId: string; contactIds: string[] }) =>
			TheBigPOSApi.inviteLoanContacts(data.loanId, data.contactIds),
	})

	const sendInviteReminderMutation = useMutation({
		mutationFn: (userId: string) =>
			TheBigPOSApi.sendLoanUserInviteReminderNotification(
				loanId ?? '',
				userId
			),
	})

	const resendInvite = useCallback(
		async (data: Invite) => {
			const { id, emailAddress } = data
			try {
				await resendInviteMutation.mutateAsync(id)
				alert(
					formatMessage(
						{ id: 'alerts.invite_success_to' },
						{ emailAddress }
					),
					{ severity: 'success' }
				)
			} catch (e) {
				alert(getErrorMessage(e), { severity: 'error' })
			}
		},
		[alert, formatMessage, resendInviteMutation]
	)

	const inviteContacts = useCallback(
		async (data: { loanId: string; contactIds: string[] }) => {
			try {
				await inviteContactsMutation.mutateAsync(data)
				alert(formatMessage({ id: 'alerts.invites_success' }), {
					severity: 'success',
				})
			} catch (e) {
				alert(getErrorMessage(e), {
					severity: 'error',
				})
			}
		},
		[alert, formatMessage, inviteContactsMutation]
	)
	return {
		loanInvitesQuery,
		resendInvite,
		resendInviteMutation,
		inviteContacts,
		inviteContactsMutation,
		sendInviteReminderMutation,
	}
}

export default useLoanInvites
